import {
	type DeliveryMethodUi,
	debugFieldClass,
	debugLabelClass,
	type GetVendorsFilteredDebugFormState,
	type LocationMode,
} from "./get-vendors-filtered-debug.utils";

type Props = {
	form: GetVendorsFilteredDebugFormState;
	update: <K extends keyof GetVendorsFilteredDebugFormState>(
		key: K,
		value: GetVendorsFilteredDebugFormState[K],
	) => void;
};

/**
 * Form fields matching `GetVendorsFilteredInput` (UI delivery mapping).
 */
export function GetVendorsFilteredDebugFormFields({ form, update }: Props) {
	return (
		<>
			<div className="grid grid-cols-2 gap-2">
				<label className={debugLabelClass}>
					page
					<input
						className={debugFieldClass}
						type="number"
						min={0}
						value={form.page}
						onChange={(e) => update("page", e.target.value)}
					/>
				</label>
				<label className={debugLabelClass}>
					pageSize
					<input
						className={debugFieldClass}
						type="number"
						min={1}
						value={form.pageSize}
						onChange={(e) => update("pageSize", e.target.value)}
					/>
				</label>
			</div>
			<label className={debugLabelClass}>
				delivery method (UI)
				<select
					className={debugFieldClass}
					value={form.deliveryMethodUi}
					onChange={(e) =>
						update("deliveryMethodUi", e.target.value as DeliveryMethodUi)
					}
				>
					<option value="none">none</option>
					<option value="shipping">shipping</option>
					<option value="courier">courier (+ express-courier)</option>
					<option value="pickup">pickup</option>
				</select>
			</label>
			<label className="flex items-center gap-2 text-body-sm">
				<input
					type="checkbox"
					checked={form.expressCourierOnly}
					onChange={(e) => update("expressCourierOnly", e.target.checked)}
				/>
				express-courier only
			</label>
			<label className="flex items-center gap-2 text-body-sm">
				<input
					type="checkbox"
					checked={form.combinedPaymentEnabled}
					onChange={(e) => update("combinedPaymentEnabled", e.target.checked)}
				/>
				filter.combinedPaymentEnabled
			</label>
			<label className={debugLabelClass}>
				filter.isTop
				<select
					className={debugFieldClass}
					value={form.isTop}
					onChange={(e) =>
						update(
							"isTop",
							e.target.value as GetVendorsFilteredDebugFormState["isTop"],
						)
					}
				>
					<option value="any">any</option>
					<option value="true">true (top only)</option>
					<option value="false">false (non-top)</option>
				</select>
			</label>
			<label className={debugLabelClass}>
				filter.maxDistance (meters)
				<input
					className={debugFieldClass}
					type="number"
					min={1}
					value={form.maxDistance}
					onChange={(e) => update("maxDistance", e.target.value)}
					placeholder="optional"
				/>
			</label>
			<label className={debugLabelClass}>
				location
				<select
					className={debugFieldClass}
					value={form.locationMode}
					onChange={(e) =>
						update("locationMode", e.target.value as LocationMode)
					}
				>
					<option value="none">none</option>
					<option value="address">address (DeliveryAddress)</option>
				</select>
			</label>
			{form.locationMode === "address" && (
				<div className="grid grid-cols-1 gap-2">
					<label className={debugLabelClass}>
						postalCode
						<input
							className={debugFieldClass}
							value={form.postalCode}
							onChange={(e) => update("postalCode", e.target.value)}
						/>
					</label>
					<div className="grid grid-cols-2 gap-2">
						<label className={debugLabelClass}>
							streetName
							<input
								className={debugFieldClass}
								value={form.streetName}
								onChange={(e) => update("streetName", e.target.value)}
							/>
						</label>
						<label className={debugLabelClass}>
							houseNr
							<input
								className={debugFieldClass}
								value={form.houseNr}
								onChange={(e) => update("houseNr", e.target.value)}
							/>
						</label>
					</div>
					<label className={debugLabelClass}>
						city
						<input
							className={debugFieldClass}
							value={form.city}
							onChange={(e) => update("city", e.target.value)}
						/>
					</label>
					<label className={debugLabelClass}>
						streetAdditional
						<input
							className={debugFieldClass}
							value={form.streetAdditional}
							onChange={(e) => update("streetAdditional", e.target.value)}
							placeholder="optional"
						/>
					</label>
				</div>
			)}
			<div className="grid grid-cols-2 gap-2">
				<label className={debugLabelClass}>
					sort.sortBy
					<select
						className={debugFieldClass}
						value={form.sortBy}
						onChange={(e) =>
							update(
								"sortBy",
								e.target.value as GetVendorsFilteredDebugFormState["sortBy"],
							)
						}
					>
						<option value="name">name</option>
						<option value="freeFrom">freeFrom</option>
						<option value="distance">distance</option>
					</select>
				</label>
				<label className={debugLabelClass}>
					sort.sortOrder
					<select
						className={debugFieldClass}
						value={form.sortOrder}
						onChange={(e) =>
							update(
								"sortOrder",
								e.target.value as GetVendorsFilteredDebugFormState["sortOrder"],
							)
						}
					>
						<option value="asc">asc</option>
						<option value="desc">desc</option>
					</select>
				</label>
			</div>
		</>
	);
}
